import React, { useEffect, useState } from 'react';
import { TextField, Select, MenuItem, InputLabel, FormControl, Stack, Grid } from '@mui/material';
import { DatePicker, LoadingButton, LocalizationProvider } from '@mui/lab';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { styled } from '@mui/system';

interface CustomLoggersModalProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filterOptions: any) => void;
}

const CustomLoggerFilterForm: React.FC<CustomLoggersModalProps> = ({
  open,
  onClose,
  onApplyFilters,
}) => {
  const [error, setError] = useState(false);
  const [filterOptions, setFilterOptions] = useState<any>({
    method: [],
    statusCode: [],
    email: '',
    model: [],
    controllerName: [],
    sortedBy: '',
    fromDate: '',
    toDate: '',
  });

  const handleChangeSelect = (event: any) => {
    const { name, value } = event.target;
    if (name === 'method' || name === 'statusCode' || name === 'controllerName') {
      const selectedValues = value as string[];

      setFilterOptions((prevFilterOptions: any) => ({
        ...prevFilterOptions,
        [name as string]: selectedValues,
      }));
    }
  };

  const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { name, value } = event.target;

    if (name === 'method' || name === 'statusCode' || name === 'model') {
      const selectedValues = value as string[];

      setFilterOptions((prevFilterOptions: any) => ({
        ...prevFilterOptions,
        [name as string]: selectedValues,
      }));
    } else {
      const newValue = value as string;
      const isValidDate =
        new String(new Date(newValue)) !== 'Invalid Date' && !isNaN(new Date(newValue).getTime());

      setFilterOptions((prevFilterOptions: any) => ({
        ...prevFilterOptions,
        [name as string]: newValue,
      }));

      if (name === 'fromDate' && isValidDate) {
        if (new Date(newValue) > new Date(filterOptions.toDate)) {
          setExpiryDateError('From date cannot be greater than the to date');
        } else {
          setExpiryDateError('');
        }
      } else if (name === 'toDate' && isValidDate) {
        if (new Date(newValue) < new Date(filterOptions.fromDate)) {
          setExpiryDateError('To date cannot be smaller than the from date');
        } else {
          setExpiryDateError('');
        }
      }
    }
  };

  const handleApplyFilters = () => {
    if (new Date(filterOptions?.fromDate) > new Date(filterOptions?.toDate)) {
      setError(true);
    } else {
      onApplyFilters(filterOptions);
      onClose();
      setError(false);
    }
  };

  const [expiryDateError, setExpiryDateError] = useState<string>('');

  const StyledTextField = styled(TextField)({
    '& .MuiFormHelperText-root': {
      color: expiryDateError ? 'red' : 'inherit',
    },
  });

  return (
    <div>
      {error && <div style={{ color: 'red' }}>error in date</div>}
      {/* #4: trimmed to the filters useful for an admin activity monitor —
          who (email), where (section), what action (method) and when (dates).
          The raw HTTP status-code filter was removed as noise. */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Action</InputLabel>
            <Select
              multiple
              name="method"
              label="Action"
              value={filterOptions.method}
              onChange={handleChangeSelect}
            >
              <MenuItem value="GET">View (GET)</MenuItem>
              <MenuItem value="POST">Create (POST)</MenuItem>
              <MenuItem value="PATCH">Update (PATCH)</MenuItem>
              <MenuItem value="PUT">Replace (PUT)</MenuItem>
              <MenuItem value="DELETE">Delete (DELETE)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Section</InputLabel>
            <Select
              multiple
              name="controllerName"
              label="Section"
              value={filterOptions.controllerName}
              onChange={handleChangeSelect}
            >
              <MenuItem value="CategoriesController">Path</MenuItem>
              <MenuItem value="TracksController">Track</MenuItem>
              <MenuItem value="CoursesController">Course</MenuItem>
              <MenuItem value="UsersController">Users</MenuItem>
              <MenuItem value="InstructorsController">Instructors</MenuItem>
              <MenuItem value="AuthController">Auth</MenuItem>
              <MenuItem value="PackagesController">Packages</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TextField
        label="User email"
        name="email"
        value={filterOptions.email}
        onChange={handleChange}
        fullWidth
        margin="normal"
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date From"
              inputFormat="MM/DD/YYYY"
              value={filterOptions.fromDate}
              onChange={(value: any) => {
                setFilterOptions((prevFilterOptions: any) => ({
                  ...prevFilterOptions,
                  fromDate: new String(value),
                }));
              }}
              renderInput={(params) => <StyledTextField {...params} error={false} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date To"
              inputFormat="MM/DD/YYYY"
              value={filterOptions.toDate}
              onChange={(value: any) => {
                setFilterOptions((prevFilterOptions: any) => ({
                  ...prevFilterOptions,
                  toDate: new String(value),
                }));
              }}
              renderInput={(params) => <StyledTextField {...params} error={false} />}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Stack alignItems="flex-end" sx={{ mt: 3 }}>
        <LoadingButton
          style={{ width: '200px', height: '50px' }}
          type="submit"
          variant="contained"
          onClick={handleApplyFilters}
        >
          {'Apply'}
        </LoadingButton>
      </Stack>
    </div>
  );
};

export default CustomLoggerFilterForm;
